import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { 
  Home, BookOpen, Clock, Wind, Sparkles, Heart, FileText, 
  Calendar, User, LayoutDashboard, LogOut, Menu, X, Users, 
  ShoppingCart, MessageSquare, Megaphone, Shield, Settings,
  ChevronDown, Leaf, Sun, Image, Share2, Package, TrendingUp, Mail,
  ShoppingBag
} from 'lucide-react';

const StarField = () => (
  <div className="absolute inset-0 overflow-hidden pointer-events-none">
    {[...Array(50)].map((_, i) => (
      <div
        key={i}
        className="absolute rounded-full bg-cosmic-starWhite"
        style={{
          width: Math.random() * 3 + 1 + 'px',
          height: Math.random() * 3 + 1 + 'px',
          top: Math.random() * 100 + '%',
          left: Math.random() * 100 + '%',
          animation: `twinkle ${Math.random() * 3 + 2}s ease-in-out ${Math.random() * 2}s infinite`,
          opacity: Math.random() * 0.7 + 0.3,
        }}
      />
    ))}
  </div>
);

const NebulaBG = () => (
  <>
    <div 
      className="absolute top-20 left-10 w-96 h-96 rounded-full opacity-20 pointer-events-none"
      style={{
        background: 'radial-gradient(ellipse at center, rgba(155, 89, 182, 0.4) 0%, transparent 70%)',
        filter: 'blur(40px)',
      }}
    />
    <div 
      className="absolute bottom-40 right-20 w-80 h-80 rounded-full opacity-15 pointer-events-none"
      style={{
        background: 'radial-gradient(ellipse at center, rgba(233, 30, 140, 0.4) 0%, transparent 70%)',
        filter: 'blur(50px)',
      }}
    />
    <div 
      className="absolute top-1/2 left-1/3 w-64 h-64 rounded-full opacity-10 pointer-events-none"
      style={{
        background: 'radial-gradient(ellipse at center, rgba(0, 206, 209, 0.5) 0%, transparent 70%)',
        filter: 'blur(30px)',
      }}
    />
  </>
);

// Dropdown component for nav menus
const NavDropdown = ({ label, icon: Icon, items, currentColors, location }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const timeoutRef = useRef(null);
  
  const isActiveGroup = items.some(item => location.pathname === item.to);
  
  const handleMouseEnter = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setIsOpen(true);
  };
  
  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => setIsOpen(false), 150);
  };
  
  return (
    <div 
      className="relative" 
      ref={dropdownRef}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <button
        className="flex items-center space-x-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-300"
        style={{
          backgroundColor: isActiveGroup ? `${currentColors.primary}20` : 'transparent',
          color: isActiveGroup ? currentColors.primary : currentColors.textMuted,
          border: isActiveGroup ? `1px solid ${currentColors.primary}40` : '1px solid transparent'
        }}
        data-testid={`nav-dropdown-${label.toLowerCase()}`}
      >
        <Icon className="w-4 h-4" />
        <span>{label}</span>
        <ChevronDown className={`w-3.5 h-3.5 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      
      {isOpen && (
        <div 
          className="absolute top-full left-0 mt-1 py-2 rounded-lg shadow-xl min-w-[180px] z-50"
          style={{ 
            backgroundColor: currentColors.background,
            border: `1px solid ${currentColors.primary}30`,
            backdropFilter: 'blur(12px)'
          }}
        >
          {items.map((item) => {
            const ItemIcon = item.icon;
            const isActive = location.pathname === item.to;
            return (
              <Link
                key={item.to}
                to={item.to}
                className="flex items-center space-x-2.5 px-4 py-2.5 text-sm transition-all hover:opacity-80"
                style={{
                  backgroundColor: isActive ? `${currentColors.primary}15` : 'transparent',
                  color: isActive ? currentColors.primary : currentColors.text
                }}
                data-testid={`nav-${item.label.toLowerCase()}`}
              >
                <ItemIcon className="w-4 h-4" />
                <span>{item.label}</span>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default function Layout({ children }) {
  const { user, logout, cartCount } = useAuth();
  const { currentColors } = useTheme();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const isAdmin = ['super_admin', 'user_admin', 'content_admin', 'practitioner_admin'].includes(user?.role);
  const isPractitioner = user?.role === 'practitioner';

  // Main navigation items - shown directly in navbar (without Shop since it's in icons)
  const mainNavItems = [
    { to: '/dashboard', icon: Home, label: 'Dashboard' },
    { to: '/healing-sessions', icon: Calendar, label: 'Sessions' },
    { to: '/community', icon: MessageSquare, label: 'Community' },
  ];
  
  // Combined Meditation & Wellness dropdown items
  const meditationItems = [
    { to: '/meditations', icon: BookOpen, label: 'Meditations' },
    { to: '/breathing', icon: Wind, label: 'Breathing' },
    { to: '/timer', icon: Clock, label: 'Timer' },
    { to: '/affirmations', icon: Sparkles, label: 'Affirmations' },
    { to: '/journal', icon: Heart, label: 'Journal' },
    { to: '/articles', icon: FileText, label: 'Articles' },
  ];

  // Admin navigation items - Now shown in Admin dropdown
  const adminDropdownItems = [];
  
  // Admin Dashboard is always first for admins
  if (isAdmin) {
    adminDropdownItems.push({ to: '/admin', icon: LayoutDashboard, label: 'Dashboard' });
  }
  
  // User management
  if (user?.role === 'super_admin' || user?.role === 'user_admin') {
    adminDropdownItems.push({ to: '/admin/users', icon: Users, label: 'Users' });
    adminDropdownItems.push({ to: '/admin/support', icon: MessageSquare, label: 'Support Center' });
  }
  
  // Content management
  if (user?.role === 'super_admin' || user?.role === 'content_admin') {
    adminDropdownItems.push({ to: '/admin/content', icon: FileText, label: 'Content' });
    adminDropdownItems.push({ to: '/admin/products', icon: Package, label: 'Products' });
    adminDropdownItems.push({ to: '/admin/sales', icon: TrendingUp, label: 'Sales' });
    adminDropdownItems.push({ to: '/admin/theme', icon: Sun, label: 'Theme' });
  }
  
  // Practitioner management
  if (user?.role === 'super_admin' || user?.role === 'practitioner_admin') {
    adminDropdownItems.push({ to: '/admin/practitioners', icon: Shield, label: 'Practitioners' });
  }
  
  // Marketing tools for admins and practitioners
  if (user?.role === 'super_admin' || user?.role === 'content_admin' || user?.role === 'practitioner_admin' || user?.role === 'practitioner') {
    adminDropdownItems.push({ to: '/admin/marketing', icon: Image, label: 'Marketing' });
    adminDropdownItems.push({ to: '/admin/social', icon: Share2, label: 'Social Media' });
  }
  
  // Practitioner's own dashboard
  if (isPractitioner) {
    adminDropdownItems.push({ to: '/practitioner/dashboard', icon: Megaphone, label: 'My Practice' });
  }

  // Legacy adminNavItems - keep empty for backward compatibility
  const adminNavItems = [];

  const getRoleBadge = (role) => {
    const badges = {
      super_admin: { label: 'Super Admin', color: 'bg-chakra-crown' },
      user_admin: { label: 'User Admin', color: 'bg-chakra-third' },
      content_admin: { label: 'Content Admin', color: 'bg-chakra-throat' },
      practitioner_admin: { label: 'Practitioner Admin', color: 'bg-chakra-heart' },
      practitioner: { label: 'Practitioner', color: 'bg-cosmic-turquoise' },
      user: { label: 'Member', color: 'bg-cosmic-purple' },
    };
    return badges[role] || badges.user;
  };

  const badge = getRoleBadge(user?.role);

  return (
    <div className="min-h-screen cosmic-bg relative">
      <StarField />
      <NebulaBG />
      
      {/* Header with theme-aware styling */}
      <header 
        className="sticky top-0 z-50 border-b backdrop-blur-xl"
        style={{ 
          backgroundColor: `${currentColors.background}cc`,
          borderColor: `${currentColors.primary}30`
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            {/* Logo */}
            <Link to="/dashboard" className="flex items-center group" data-testid="logo-link">
              <div className="relative flex-shrink-0">
                <img 
                  src="/assets/logo.png" 
                  alt="The Aura Space" 
                  className="h-20 w-auto object-contain lotus-glow transition-transform group-hover:scale-105"
                  style={{ minWidth: '70px' }}
                />
              </div>
            </Link>

            {/* Desktop Navigation - All items visible */}
            <nav className="hidden xl:flex items-center space-x-1.5">
              {mainNavItems.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.to;
                return (
                  <Link
                    key={item.to}
                    to={item.to}
                    data-testid={`nav-${item.label.toLowerCase()}`}
                    className="flex items-center space-x-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-300"
                    style={{
                      backgroundColor: isActive ? `${currentColors.primary}20` : 'transparent',
                      color: isActive ? currentColors.primary : currentColors.textMuted,
                      border: isActive ? `1px solid ${currentColors.primary}40` : '1px solid transparent'
                    }}
                  >
                    <Icon className="w-4 h-4" />
                    <span>{item.label}</span>
                  </Link>
                );
              })}
              
              {/* Meditation Dropdown - Combined with Wellness */}
              <NavDropdown 
                label="Meditation" 
                icon={Sun} 
                items={meditationItems} 
                currentColors={currentColors} 
                location={location} 
              />

              {/* Contact Link */}
              <Link 
                to="/contact"
                data-testid="nav-contact"
                className="flex items-center space-x-1 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-all duration-300"
                style={{
                  backgroundColor: location.pathname === '/contact' ? `${currentColors.primary}20` : 'transparent',
                  color: location.pathname === '/contact' ? currentColors.primary : currentColors.accent,
                  border: location.pathname === '/contact' ? `1px solid ${currentColors.primary}40` : '1px solid transparent'
                }}
              >
                <Mail className="w-3.5 h-3.5" />
                <span>Contact</span>
              </Link>
              
              {/* Admin Dropdown - Contains all admin links */}
              {(isAdmin || isPractitioner) && adminDropdownItems.length > 0 && (
                <NavDropdown 
                  label="Admin" 
                  icon={LayoutDashboard} 
                  items={adminDropdownItems} 
                  currentColors={currentColors} 
                  location={location} 
                />
              )}
            </nav>

            {/* Right side: Home + Practitioner + Shop + Cart + User */}
            <div className="flex items-center space-x-2 sm:space-x-3">
              {/* Home Button */}
              <Link 
                to="/dashboard" 
                className="p-2 rounded-lg transition-all hover:scale-105 relative z-50"
                style={{ 
                  backgroundColor: location.pathname === '/dashboard' ? `${currentColors.primary}20` : `${currentColors.primary}10`,
                  color: currentColors.primary
                }}
                data-testid="home-button"
                title="Home Dashboard"
                onClick={(e) => {
                  e.stopPropagation();
                }}
              >
                <Home className="w-5 h-5" />
              </Link>

              {/* Practitioner Space Button - Only for practitioners */}
              {isPractitioner && (
                <Link 
                  to="/practitioner/dashboard" 
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-full transition-all hover:scale-105"
                  style={{ 
                    background: `linear-gradient(135deg, #8B5CF6, #A855F7)`,
                    boxShadow: `0 2px 10px rgba(139, 92, 246, 0.4)`
                  }}
                  data-testid="practitioner-button"
                  title="My Practice"
                >
                  <Sparkles className="w-4 h-4 text-white" />
                  <span className="text-white text-sm font-medium hidden sm:inline">My Practice</span>
                </Link>
              )}

              {/* Shop Button with Spiritual Aura Animation */}
              <Link 
                to="/shop" 
                className="shop-aura-btn relative flex items-center gap-2 px-5 py-2.5 rounded-full transition-all hover:scale-105 overflow-visible"
                style={{ 
                  background: `linear-gradient(135deg, ${currentColors.primary}, ${currentColors.secondary})`,
                  boxShadow: `0 0 20px ${currentColors.primary}50, 0 0 40px ${currentColors.primary}30`
                }}
                data-testid="shop-button"
              >
                {/* Outer Aura Ring 1 */}
                <span 
                  className="absolute inset-0 rounded-full animate-ping opacity-30"
                  style={{ 
                    background: `linear-gradient(135deg, ${currentColors.primary}, ${currentColors.secondary})`,
                    animationDuration: '2s'
                  }}
                />
                {/* Outer Aura Ring 2 */}
                <span 
                  className="absolute -inset-1 rounded-full opacity-20"
                  style={{ 
                    background: `radial-gradient(circle, ${currentColors.primary}60 0%, transparent 70%)`,
                    animation: 'aura-pulse 2s ease-in-out infinite'
                  }}
                />
                {/* Outer Aura Ring 3 - Slower */}
                <span 
                  className="absolute -inset-2 rounded-full opacity-15"
                  style={{ 
                    background: `radial-gradient(circle, ${currentColors.secondary}40 0%, transparent 70%)`,
                    animation: 'aura-pulse 3s ease-in-out infinite 0.5s'
                  }}
                />
                {/* Inner glow */}
                <span 
                  className="absolute inset-0 rounded-full"
                  style={{ 
                    background: `radial-gradient(circle at 30% 30%, rgba(255,255,255,0.3) 0%, transparent 60%)`,
                  }}
                />
                {/* Sparkle effect */}
                <span 
                  className="absolute top-1 right-2 w-1.5 h-1.5 rounded-full bg-white opacity-80"
                  style={{ animation: 'sparkle 1.5s ease-in-out infinite' }}
                />
                <span 
                  className="absolute bottom-2 left-3 w-1 h-1 rounded-full bg-white opacity-60"
                  style={{ animation: 'sparkle 2s ease-in-out infinite 0.3s' }}
                />
                {/* Button content */}
                <ShoppingBag className="w-5 h-5 text-white relative z-10" />
                <span className="text-white text-sm font-semibold relative z-10 tracking-wide">Shop</span>
              </Link>

              {/* Cart Icon */}
              <Link 
                to="/shop" 
                className="relative p-2 rounded-lg transition-all hover:opacity-80"
                style={{ color: currentColors.primary }}
                data-testid="cart-icon"
              >
                <ShoppingCart className="w-5 h-5" />
                {cartCount > 0 && (
                  <span 
                    className="absolute -top-1 -right-1 w-4 h-4 rounded-full text-xs flex items-center justify-center"
                    style={{ backgroundColor: currentColors.accent, color: currentColors.background }}
                  >
                    {cartCount}
                  </span>
                )}
              </Link>

              {/* User menu - simplified to just icon */}
              <div className="hidden lg:flex items-center space-x-2">
                <Link
                  to="/profile"
                  className="w-10 h-10 rounded-full flex items-center justify-center transition-all hover:scale-105"
                  style={{ 
                    backgroundColor: `${currentColors.primary}20`,
                    border: `1px solid ${currentColors.primary}40`
                  }}
                  data-testid="user-profile-link"
                  title={`${user?.name} (${badge.label})`}
                >
                  <User className="w-5 h-5" style={{ color: currentColors.primary }} />
                </Link>
                
                <Link
                  to="/security"
                  className="p-2 rounded-lg transition-all hover:opacity-80"
                  style={{ color: currentColors.textMuted }}
                  data-testid="security-link"
                  title="Security Settings"
                >
                  <Shield className="w-5 h-5" />
                </Link>
                
                <button
                  onClick={logout}
                  className="p-2 rounded-lg transition-all hover:opacity-80"
                  style={{ color: currentColors.textMuted }}
                  data-testid="logout-button"
                  title="Sign Out"
                >
                  <LogOut className="w-5 h-5" />
                </button>
              </div>

              {/* Mobile menu button */}
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="xl:hidden p-2 rounded-lg"
                style={{ color: currentColors.text }}
                data-testid="mobile-menu-button"
              >
                {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div 
            className="xl:hidden border-t"
            style={{ 
              backgroundColor: currentColors.background,
              borderColor: `${currentColors.primary}20`
            }}
          >
            <div className="max-w-7xl mx-auto px-4 py-4 space-y-2">
              {mainNavItems.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.to;
                return (
                  <Link
                    key={item.to}
                    to={item.to}
                    onClick={() => setMobileMenuOpen(false)}
                    className="flex items-center space-x-3 px-4 py-3 rounded-lg transition-all"
                    style={{
                      backgroundColor: isActive ? `${currentColors.primary}20` : 'transparent',
                      color: isActive ? currentColors.primary : currentColors.text
                    }}
                  >
                    <Icon className="w-5 h-5" />
                    <span>{item.label}</span>
                  </Link>
                );
              })}
              
              {/* Meditation Section */}
              <div className="border-t my-2 pt-2" style={{ borderColor: `${currentColors.primary}20` }}>
                <p className="px-4 text-xs uppercase tracking-wider mb-2 flex items-center gap-2" style={{ color: currentColors.textMuted }}>
                  <Sun className="w-3 h-3" /> Meditation
                </p>
              </div>
              {meditationItems.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.to;
                return (
                  <Link
                    key={item.to}
                    to={item.to}
                    onClick={() => setMobileMenuOpen(false)}
                    className="flex items-center space-x-3 px-4 py-3 rounded-lg transition-all"
                    style={{
                      backgroundColor: isActive ? `${currentColors.primary}20` : 'transparent',
                      color: isActive ? currentColors.primary : currentColors.text
                    }}
                  >
                    <Icon className="w-5 h-5" />
                    <span>{item.label}</span>
                  </Link>
                );
              })}
              
              {/* Quick Actions */}
              <div className="border-t my-2 pt-2" style={{ borderColor: `${currentColors.primary}20` }}>
                <p className="px-4 text-xs uppercase tracking-wider mb-2 flex items-center gap-2" style={{ color: currentColors.textMuted }}>
                  Quick Actions
                </p>
              </div>
              <Link
                to="/shop"
                onClick={() => setMobileMenuOpen(false)}
                className="flex items-center space-x-3 px-4 py-3 rounded-lg transition-all"
                style={{
                  backgroundColor: location.pathname === '/shop' ? `${currentColors.primary}20` : 'transparent',
                  color: location.pathname === '/shop' ? currentColors.primary : currentColors.text
                }}
              >
                <ShoppingBag className="w-5 h-5" />
                <span>Shop</span>
              </Link>
              <Link
                to="/contact"
                onClick={() => setMobileMenuOpen(false)}
                className="flex items-center space-x-3 px-4 py-3 rounded-lg transition-all"
                style={{
                  backgroundColor: location.pathname === '/contact' ? `${currentColors.primary}20` : 'transparent',
                  color: location.pathname === '/contact' ? currentColors.primary : currentColors.text
                }}
              >
                <Mail className="w-5 h-5" />
                <span>Contact</span>
              </Link>

              {/* Practitioner Space - Only for practitioners */}
              {isPractitioner && (
                <Link
                  to="/practitioner/dashboard"
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex items-center space-x-3 px-4 py-3 rounded-lg transition-all"
                  style={{
                    backgroundColor: location.pathname === '/practitioner/dashboard' ? `${currentColors.primary}20` : 'transparent',
                    color: '#8B5CF6'
                  }}
                >
                  <Sparkles className="w-5 h-5" />
                  <span>My Practice</span>
                </Link>
              )}
              
              {adminDropdownItems.length > 0 && (
                <>
                  <div 
                    className="border-t my-2 pt-2"
                    style={{ borderColor: `${currentColors.primary}20` }}
                  >
                    <p className="px-4 text-xs uppercase tracking-wider mb-2" style={{ color: currentColors.textMuted }}>
                      Admin
                    </p>
                  </div>
                  {adminDropdownItems.map((item) => {
                    const Icon = item.icon;
                    const isActive = location.pathname === item.to;
                    return (
                      <Link
                        key={item.to}
                        to={item.to}
                        onClick={() => setMobileMenuOpen(false)}
                        className="flex items-center space-x-3 px-4 py-3 rounded-lg transition-all"
                        style={{
                          backgroundColor: isActive ? `${currentColors.secondary}20` : 'transparent',
                          color: isActive ? currentColors.secondary : currentColors.accent
                        }}
                      >
                        <Icon className="w-5 h-5" />
                        <span>{item.label}</span>
                      </Link>
                    );
                  })}
                </>
              )}
              
              <div 
                className="border-t mt-4 pt-4"
                style={{ borderColor: `${currentColors.primary}20` }}
              >
                <Link
                  to="/profile"
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex items-center space-x-3 px-4 py-3 rounded-lg"
                  style={{ color: currentColors.text }}
                >
                  <User className="w-5 h-5" />
                  <span>Profile</span>
                </Link>
                <Link
                  to="/security"
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex items-center space-x-3 px-4 py-3 rounded-lg"
                  style={{ color: currentColors.text }}
                >
                  <Shield className="w-5 h-5" />
                  <span>Security</span>
                </Link>
                <button
                  onClick={() => { logout(); setMobileMenuOpen(false); }}
                  className="flex items-center space-x-3 px-4 py-3 rounded-lg w-full text-left"
                  style={{ color: currentColors.textMuted }}
                >
                  <LogOut className="w-5 h-5" />
                  <span>Sign Out</span>
                </button>
              </div>
            </div>
          </div>
        )}
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 relative z-10">
        {children}
      </main>

      {/* Footer */}
      <footer 
        className="border-t mt-auto py-6"
        style={{ 
          backgroundColor: `${currentColors.background}80`,
          borderColor: `${currentColors.primary}20`
        }}
      >
        <div className="max-w-7xl mx-auto px-4 text-center">
          <p style={{ color: currentColors.textMuted }} className="text-sm">
            © 2026 The Aura Space. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}
